/* ═══════════════════════════════════════════════════════════════════════════
   THE OCCUPATIONAL HEALTH PROVIDERS

   Firms that actually sell bedriftshelsetjeneste in Norway, listed here so
   the screens have something real to show. Read the two rules below before
   adding one.

   ── No invented approval numbers ──────────────────────────────────────────

   A bedriftshelsetjeneste only discharges the duty if Arbeidstilsynet has
   approved it, and approval is a checkable fact with a register behind it.
   So every record carries the BODY and the LABEL — facts about the scheme,
   not about the firm — with `number: null` and `checked: false`. A
   plausible-looking approval number would be worse than none, because
   somebody would read it as checked. The same rule the people records keep
   for practising certificates, and for the same reason.

   ── No invented prices ────────────────────────────────────────────────────

   Not ours and not theirs. None of these firms has been asked yet, nothing
   is signed, and a figure on a screen has a way of turning into a figure on
   a quote. `price: null` means the screen says "not agreed with this
   provider yet" — which is the truth, and which is also the sentence that
   makes the commercial conversation easy to have rather than awkward.

   ── And none of them is on board ──────────────────────────────────────────

   `state: 'not_contacted'` on every one. Nothing here implies a partnership
   that does not exist, on screen or anywhere else. The demo is a demo and
   says so.
   ═══════════════════════════════════════════════════════════════════════ */

const SCHEME = {
  body: 'Arbeidstilsynet',
  label: 'Approved occupational health service',
  number: null,
  checked: false,
};

export const PROVIDERS = [
  {
    id: 'avonova',
    name: 'Avonova',
    lead: 'One of the largest in the Nordics, with departments across Norway.',
    reach: 'Nationwide, with physical departments',
    ways: ['video', 'clinic', 'on site'],
    does: ['Sykefraværsoppfølging', 'Workplace assessment', 'Health checks',
           'Ergonomics', 'Psychosocial work environment'],
    approval: { ...SCHEME },
    price: null,
    state: 'not_contacted',
  },
  {
    id: 'drdropin',
    name: 'Dr.Dropin',
    lead: 'Digital-first, with clinics in the larger towns. Video is the '
      + 'default rather than the exception.',
    reach: 'Video everywhere, clinics in the larger towns',
    ways: ['video', 'clinic'],
    does: ['Sykefraværsoppfølging', 'Health checks', 'GP appointments',
           'Psychologist', 'Physiotherapy'],
    approval: { ...SCHEME },
    price: null,
    state: 'not_contacted',
  },
  {
    id: 'falck',
    name: 'Falck Helse',
    lead: 'Occupational health alongside emergency-preparedness and first-aid '
      + 'training.',
    reach: 'Nationwide',
    ways: ['video', 'clinic', 'on site'],
    does: ['Sykefraværsoppfølging', 'Workplace assessment', 'First-aid training',
           'Health checks', 'Noise and air measurement'],
    approval: { ...SCHEME },
    price: null,
    state: 'not_contacted',
  },
  {
    id: 'synergi',
    name: 'Synergi Helse',
    lead: 'Occupational health and HSE advice, strongest in the east.',
    reach: 'Eastern Norway, video nationwide',
    ways: ['video', 'clinic', 'on site'],
    does: ['Sykefraværsoppfølging', 'HSE training', 'Workplace assessment',
           'Health checks'],
    approval: { ...SCHEME },
    price: null,
    state: 'not_contacted',
  },
];

export const provider = id => PROVIDERS.find(p => p.id === id) || null;

/** What a provider can actually do down a wire. The rest needs somebody in
    the room, and the booking screen has to say which is which. */
export const remote = id => (provider(id)?.ways || []).includes('video');

/* The services an employee may book for themselves, as opposed to the ones
   the employer commissions about the workplace. The split matters: a
   workplace assessment is the business's; an appointment about your own back
   is yours, and the business is not told you had it. */
export const FOR_EMPLOYEE = [
  { id: 'sick_follow', name: 'Sykefraværsoppfølging', mins: 30, ways: ['video', 'clinic'],
    what: 'The conversation about getting back to work. Yours to ask for, not only your employer\'s.' },
  { id: 'gp', name: 'A doctor', mins: 20, ways: ['video', 'clinic'],
    what: 'Something you want looked at, work-related or not, depending on what the plan covers.' },
  { id: 'physio', name: 'Physiotherapy', mins: 30, ways: ['clinic'],
    what: 'Backs, shoulders, wrists — the ones that come with standing at a chair all day.' },
  { id: 'psych', name: 'Psychologist', mins: 45, ways: ['video', 'clinic'],
    what: 'Work, or anything else. Nobody at your workplace is told you booked it.' },
  { id: 'ergonomics', name: 'Ergonomics', mins: 30, ways: ['video', 'on site'],
    what: 'How you stand, sit and lift, looked at where you actually do it.' },
];

/* And the ones the employer commissions. These are about the workplace
   rather than about a person, so the business sees them in full — there is
   nobody's confidence to keep. */
export const FOR_BUSINESS = [
  { id: 'assessment', name: 'Workplace assessment', what: 'The walk-round, and the report that comes out of it.' },
  { id: 'plan', name: 'Annual plan and report', what: 'What the service will do this year, and what it did last.' },
  { id: 'training', name: 'HSE training', what: 'For managers and safety representatives.' },
  { id: 'measure', name: 'Measurement', what: 'Noise, air, light — where the trade calls for it.' },
];

export const employeeService = id => FOR_EMPLOYEE.find(s => s.id === id) || null;
